import React, { useRef, useEffect, useState, useMemo } from 'react';
import { World } from '@/data/worlds';

interface WorldRendererProps {
  world: World;
  paused?: boolean;
}

// V1 session videos - time-of-day aware
const SESSION_VIDEOS = {
  day: 'https://upcdn.io/FW25ant/raw/kaizen/woman.mp4',
  evening: 'https://upcdn.io/FW25ant/raw/kaizen/man.mp4',
};

const WorldRenderer: React.FC<WorldRendererProps> = ({ world, paused = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  // Determine video based on time of day (like v1)
  const isEvening = useMemo(() => new Date().getHours() >= 18, []);
  const videoSrc = isEvening ? SESSION_VIDEOS.evening : SESSION_VIDEOS.day;

  // Reset states when video source changes
  useEffect(() => {
    setVideoLoaded(false);
    setVideoError(false);
  }, [videoSrc]);

  // Handle video play/pause based on session state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (paused) {
      video.pause();
    } else {
      video.play().catch(() => {
        // Auto-play might be blocked
      });
    }
  }, [paused, videoSrc]);

  return (
    <div className="absolute inset-0">
      {/* Video background */}
      {!videoError && (
        <video
          ref={videoRef}
          src={videoSrc}
          autoPlay
          loop
          muted
          playsInline
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            videoLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoadedData={() => setVideoLoaded(true)}
          onError={() => setVideoError(true)}
        />
      )}

      {/* Fallback gradient when video not available */}
      <div
        className={`absolute inset-0 transition-opacity duration-1000 ${
          videoLoaded && !videoError ? 'opacity-0' : 'opacity-100'
        }`}
        style={{
          background: `
            radial-gradient(ellipse at 30% 20%, ${world.colorScheme.primary}40 0%, transparent 50%),
            radial-gradient(ellipse at 70% 80%, ${world.colorScheme.accent}30 0%, transparent 50%),
            linear-gradient(180deg, ${world.colorScheme.secondary} 0%, ${world.colorScheme.background} 100%)
          `,
        }}
      />

      {/* Gradient overlay for better text readability */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.8) 100%)',
        }}
      />
    </div>
  );
};

export default WorldRenderer;
